import { action, makeObservable, observable } from 'mobx';
import moment from 'moment';
import environment from '@menu-environment';
import { AppStore } from 'app.store';
import { CircleButtonSize } from 'components/buttons/button-types';
import { PopupConfig } from 'components/popup/popup-config';
import { PopupLayout, PopupStore } from 'components/popup/popup.store';
import { TabStyle } from 'components/tabs/tabs';
import { Breakpoints } from 'core/enums/breakpoints';
import { MessageModel } from 'core/models/general/inbox';
import { InboxService } from 'core/services/api/inbox';
import { PollingStore } from 'core/stores/polling.store';
import { _noop } from 'utils/function.utils';

export enum InboxSteps {
  All = "All",
  Rewards = 'Rewards',
  Offers = 'Offers',
  Account = "Account",
  Reminders = "Reminders"
}

type MessageList = { messages: MessageModel[], unreadCount: number }


export class InboxStore {
  isLoading = true;
  isLoadingMessages = true;
  isLoadingDelete = false;
  totalCount = 0;
  searchTerm = '';
  currentPage = -1;
  pageSize = 10;
  siblingCount = 1;
  customerNumber!: string;
  countryCode!: string;
  locale!: string;
  customerIdentifier!: string;
  searchOnProperties: string[] = [];
  currentMessage?: MessageModel = undefined;
  messages: MessageModel[] = [];
  activeTab = InboxSteps.All;
  currentCategory = '';
  appStore!: AppStore;
  popupConfig: PopupConfig = {
    template: PopupLayout.Error,
    hasCancelButton: true,
    hideCloseIcon: true,
    heading: 'Inbox.deleteMessageErrorPopup.heading',
  };
  popupStore = new PopupStore();
  pollingStore!: PollingStore;
  pollingChecks = {
    // NOTE: We always want to re-poll the messages so long as there isn't an API failure, or the max requests is reached
    onPending: (res: any) => true,
    onSuccess: (res: any) => false,
    onFailed: (res: any) => false,
  }

  get allMessages() {
    return this.messages.filter((message: any) => (!this.searchTerm?.length || this.filterByTerm(this.searchTerm, message)))
  }

  get rewardsMessages() {
    return this.messages.filter((message: any) => message.categoryTitle === "My Rewards" && (!this.searchTerm?.length || this.filterByTerm(this.searchTerm, message)))
  }

  get offersMessages() {
    return this.messages.filter((message: any) => message.categoryTitle === "My Offers" && (!this.searchTerm?.length || this.filterByTerm(this.searchTerm, message)))
  }

  get accountMessages() {
    return this.messages.filter((message: any) => message.categoryTitle === "My Account" && (!this.searchTerm?.length || this.filterByTerm(this.searchTerm, message)))
  }

  get reminderMessages() {
    return this.messages.filter((message: any) => message.categoryTitle === "My Reminders" && (!this.searchTerm?.length || this.filterByTerm(this.searchTerm, message)))
  }

  get activeMessages() {
    switch (this.activeTab) {
      case InboxSteps.Rewards:
        return this.rewardsMessages.slice(this.messageOffset, this.messageSize);
      case InboxSteps.Offers:
        return this.offersMessages.slice(this.messageOffset, this.messageSize);
      case InboxSteps.Account:
        return this.accountMessages.slice(this.messageOffset, this.messageSize);
      case InboxSteps.Reminders:
        return this.reminderMessages.slice(this.messageOffset, this.messageSize);
      default:
        return this.allMessages.slice(this.messageOffset, this.messageSize);
    }
  }

  get messageOffset() {
    return (this.currentPage - 1) * this.pageSize;
  }

  get messageSize() {
    return this.pageSize * this.currentPage;
  }

  get totalPages() {
    switch (this.activeTab) {
      case InboxSteps.All:
        return this.allMessages.length;
      case InboxSteps.Rewards:
        return this.rewardsMessages.length;
      case InboxSteps.Offers:
        return this.offersMessages.length;
      case InboxSteps.Account:
        return this.accountMessages.length;
      case InboxSteps.Reminders:
        return this.reminderMessages.length;
      default:
        return 0;
    }
  }

  constructor() {
    makeObservable(this, {
      isLoading: observable,
      isLoadingMessages: observable,
      isLoadingDelete: observable,
      customerNumber: observable,
      customerIdentifier: observable,
      countryCode: observable,
      activeTab: observable,
      searchTerm: observable,
      locale: observable,
      currentMessage: observable,
      messages: observable,
      totalCount: observable,
      currentPage: observable,
      pageSize: observable,
      siblingCount: observable,
      setCurrentCategory: action,
      changeIsLoading: action,
      changeIsLoadingDelete: action,
      changeMessages: action,
      changeActiveTab: action,
      changeCurrentPage: action,
      changeSearchTerm: action,
      changeCustomerNumber: action,
      changeCountryCode: action,
      changeCustomerIdentifier: action,
      changeLocale: action,
    });
  }

  initialize(appStore: AppStore, searchOnProperties?: string[]) {
    this.searchOnProperties = searchOnProperties ?? [];
    this.appStore = appStore;
    this.changeCustomerNumber(appStore.customerNumber);
    this.changeCountryCode(appStore.countryCodeThreeChar);
    this.changeLocale(appStore.locale);
    this.getInboxMessages().catch(_noop)
    this.popupStore.initialize(this.popupConfig);

    this.pollingStore = new PollingStore(this.getInboxMessages, this.pollingChecks);
    this.pollingStore.changeLimits({
      MaxTime: environment.INBOX_POLL_TIME_MAX,
      MaxErrors: 1,
      PollTimeStart: environment.INBOX_POLL_TIME_START
    })
  }

  startPoll = () => {
    this.pollingStore.startPoll();
  }

  getInboxMessages = () => {
    this.changeIsLoading(true);
    return InboxService.getInboxData(this.customerNumber, this.countryCode)
      .then((inboxMessages) => {
        this.changeCustomerIdentifier(inboxMessages.customerIdentifier);

        if (!inboxMessages?.Categories?.length) {
          // NOTE: if there are no messages returned just resolve- this allows polling to continue.
          return Promise.resolve();
        }

        const messageList = inboxMessages.Categories?.reduce((messageAccumulator: MessageList, current): MessageList => {

          const mappedMessages = current.messages.map((msg: MessageModel) => {
            const localTimestamp = moment.utc(msg.timestamp).local();
            return {
              categoryTitle: current.categoryTitle,
              categoryID: current.categoryID,
              ...msg,
              timestamp: localTimestamp,
              dateTime: localTimestamp.format('DD MMM YYYY HH:mm'),
              date: localTimestamp.format('DD MMM YYYY'),
              time: localTimestamp.format('HH:mm'),
            }
          });

          return {
            messages: [
              ...messageAccumulator.messages,
              ...mappedMessages
            ],
            unreadCount: messageAccumulator.unreadCount + +(current.unreadMessagesCount)
          };
        },
          {
            messages: [],
            unreadCount: 0
          } as MessageList
        );

        if (messageList.messages.length > 0) {
          this.changeCurrentPage(1);
        }

        this.appStore.changeInboxDisplay({
          display: true,
          unread: messageList.unreadCount,
          url: `${environment.APP_ROOT_URL.replace('{culture}', this.locale)}#/inbox`
        });

        this.changeMessages(
          messageList.messages.sort((a, b) => b.timestamp.isSameOrBefore(a.timestamp) ? -1 : 1)
        );

        return Promise.resolve();
      })
      .catch((error) => {
        console.error(error);
        return Promise.reject('Get messages failed');
      })
      .finally(() => this.changeIsLoading(false));
  }

  markAsRead = (message: MessageModel) => {
    InboxService.readInboxMessage(
      this.customerNumber,
      {
        categoryID: message.categoryID ?? '',
        countryCode: this.countryCode,
        customerIdentifier: this.customerNumber,
        RequestDetails: [{
          multichoiceMessageID: message.multichoiceMessageID,
          isRead: String(true)
        }]
      }
    )
      .then((res) => {
        const hasSuccess = res?.ResponseDetails?.some(responseDetail => responseDetail.status === 'SUCCESS');

        if (!hasSuccess) {
          console.error('Failed to mark message as read.', res);
          return;
        }

        const unreadMessages = this.appStore.inboxDisplay;

        if (unreadMessages) {
          this.appStore.changeInboxDisplay({
            ...unreadMessages,
            unread: --unreadMessages.unread
          });
        }
      })
      .catch((error) => console.error(error));
  }

  deleteMessage = (message?: MessageModel) => {
    if (!message) {
      console.error('Cannot deleted undefined message.')
      return;
    }
    this.changeIsLoadingDelete(true);

    InboxService.deleteInboxMessage(
      this.customerNumber,
      {
        categoryID: message.categoryID ?? '',
        countryCode: this.countryCode,
        customerIdentifier: this.customerNumber,
        RequestDetails: [{
          multichoiceMessageID: message.multichoiceMessageID,
          isDelete: String(true)
        }]
      }
    )
      .then((res) => {
        const hasSuccess = res?.ResponseDetails?.some(responseDetail => responseDetail.status === 'SUCCESS');

        if (!hasSuccess) {
          this.popupStore.changeIsOpen(true);
          console.error('Failed to deleted message.', res);
          return;
        }

        this.changeCurrentMessage(undefined);
        this.getInboxMessages();
      })
      .catch(() => {
        this.popupStore.changeIsOpen(true);
      })
      .finally(() => {
        this.changeIsLoadingDelete(false);
      });
  }

  changeIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  }

  changeIsLoadingDelete = (isLoading: boolean) => {
    this.isLoadingDelete = isLoading;
  }

  changeMessages(messages: MessageModel[]) {
    this.messages = messages;
  }

  changeCurrentMessage = (message?: MessageModel) => {
    this.currentMessage = message;
  }

  changeCurrentPage = (page: number) => {
    this.currentPage = page;
  }

  changeCustomerNumber(customerNumber: string) {
    this.customerNumber = customerNumber;
  }

  changeCountryCode(countryCode: string) {
    this.countryCode = countryCode;
  }

  changeLocale(locale: string) {
    this.locale = locale;
  }

  changeCustomerIdentifier(identifier: string) {
    this.customerIdentifier = identifier;
  }

  changeActiveTab(activeTab: any) {
    this.activeTab = activeTab;
  }

  changeSearchTerm(search: string) {
    this.searchTerm = search;
  }

  filterByTerm(term = '', message: MessageModel) {
    return this.searchOnProperties.some(
      (prop) =>
        `${(message as { [key: string]: any })[prop]}`.toLowerCase().includes(term.toLowerCase()) ||
        `${(message.content as { [key: string]: any })[prop]}`.toLowerCase().includes(term.toLowerCase())
    );
  }

  getListConfig(screenSize: number) {
    return {
      style: screenSize <= Breakpoints.MobileStart ? TabStyle.CircleButtonTab : TabStyle.Standard,
      size: screenSize >= Breakpoints.MobileExtraSmallEnd ? CircleButtonSize.ExtraLarge : CircleButtonSize.Large,
    };
  }

  setCurrentCategory(categoryVal: string) {
    const category = categoryVal
    this.currentCategory = category;
  }
}